import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { SpriteIcon } from '@/components/SpriteIcon';
import type { MfaManageResponse } from '@/api/types';
import { AuthCard, AuthErrorSummary } from './AuthCard';
import { AuthField } from './AuthField';
import { SubmitButton } from './SubmitButton';
import { mfaCodeSchema, type MfaCodeValues } from './schemas';
import { useServerValidation } from './useServerValidation';
import { useEnableMfa, useMfaStatus, useResetAuthenticatorKey } from './useMfa';
import { RecoveryCodeList } from './RecoveryCodeList';

const FIELDS = ['code'] as const;

type Stage = 'starting' | 'confirmPassword' | 'scan' | 'recoveryCodes';

/**
 * Authenticator enrolment, reached voluntarily from the profile or compulsorily
 * by `RequireAuth` when the user's role mandates a second factor.
 *
 * The mandatory variant follows `ChangePasswordPage`: a warning replaces the
 * subtitle and the Cancel link disappears, because there is nowhere to cancel to
 * — the guard would send the user straight back.
 *
 * There is no method chooser: an authenticator app is the only supported second
 * factor. A first-time enrolment therefore goes straight to the QR code; only a
 * user replacing an authenticator that already works is stopped for a password
 * first, because that is the one case where starting destroys something.
 */
export function MfaSetupPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const { data: status, isLoading } = useMfaStatus();
  const resetKey = useResetAuthenticatorKey();
  const enableMfa = useEnableMfa();

  const [stage, setStage] = useState<Stage>('starting');
  const [secret, setSecret] = useState<MfaManageResponse | null>(null);
  const [password, setPassword] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const isMandatory = status?.isMandatory ?? false;

  const schema = useMemo(() => mfaCodeSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MfaCodeValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
  });

  const { formErrors, clearFormErrors, showFormError, applyServerErrors } = useServerValidation(
    setError,
    FIELDS,
  );

  const startKeySetup = async (confirmedPassword?: string) => {
    clearFormErrors();
    reset({ code: '' });

    try {
      const result = await resetKey.mutateAsync(confirmedPassword);
      setSecret(result);
      setPassword('');
      setStage('scan');
    } catch (error) {
      applyServerErrors(error);
    }
  };

  /**
   * Mint the secret as soon as we know the user is not replacing a working
   * authenticator. Safe to do unprompted in that case: there is no old secret to
   * destroy, and nothing is enabled until a code is verified.
   *
   * The ref guards against StrictMode running this effect twice, which would
   * mint two secrets and leave the QR on screen showing the losing one.
   */
  const started = useRef(false);
  useEffect(() => {
    if (!status || started.current) return;
    started.current = true;

    if (status.isTwoFactorEnabled) {
      setStage('confirmPassword');
      return;
    }

    void startKeySetup(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onSubmit = handleSubmit(async (values) => {
    clearFormErrors();

    try {
      const result = await enableMfa.mutateAsync(values.code);

      // Refresh before going anywhere: while mustSetupMfa is still cached as
      // true, the guard would bounce any destination straight back here.
      await refresh();

      // A fresh set is returned only on a first enrolment. Re-enrolling on a new
      // phone keeps the codes the user already has, so there is nothing to show
      // and nothing to interrupt them with.
      const issued = result.twoFactor.recoveryCodes;
      if (issued && issued.length > 0) {
        setRecoveryCodes(issued);
        setStage('recoveryCodes');
        return;
      }

      navigate('/', { replace: true });
    } catch (error) {
      reset({ code: '' });
      applyServerErrors(error);
    }
  });

  if (isLoading || !status) {
    return (
      <AuthCard centered>
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        {t('Loading', 'Loading...')}
      </AuthCard>
    );
  }

  if (!status.featureEnabled) {
    return (
      <AuthCard title={t('auth:Auth.MFA.SetupTitle', 'Set up two-step verification')}>
        <p className="text-muted">
          {t('auth:Auth.MFA.Unavailable', 'Two-step verification is not available.')}
        </p>
        <Link to="/user-profile" className="btn btn-outline-secondary w-100">
          {t('Back', 'Back')}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      aboveTitle={
        isMandatory && stage !== 'recoveryCodes' ? (
          <div className="alert alert-warning mb-3 d-flex align-items-start" role="alert">
            <SpriteIcon name="alert-triangle" size={20} className="sa-icon me-2 flex-shrink-0" />
            <div>
              <strong>{t('auth:Auth.MFA.SetupRequired', 'Two-step verification required')}</strong>
              <p className="mb-0 mt-2 small">
                {t(
                  'auth:Auth.MFA.SetupRequiredMessage',
                  'Your role requires a second factor. Set one up to continue.',
                )}
              </p>
            </div>
          </div>
        ) : null
      }
      title={
        stage === 'recoveryCodes'
          ? t('auth:Auth.MFA.RecoveryCodesTitle', 'Your recovery codes')
          : t('auth:Auth.MFA.SetupTitle', 'Set up two-step verification')
      }
    >
      <AuthErrorSummary errors={formErrors} />

      {stage === 'starting' && (
        <div className="text-center py-3">
          <span className="spinner-border spinner-border-sm me-2" role="status" />
          {t('Loading', 'Loading...')}
        </div>
      )}

      {stage === 'confirmPassword' && (
        <>
          <div className="alert alert-warning small d-flex align-items-start" role="alert">
            <SpriteIcon name="alert-triangle" size={16} className="sa-icon me-2 flex-shrink-0" />
            <div>
              {t(
                'auth:Auth.MFA.ReplaceWarning',
                'Setting up a new authenticator will stop your current one from working.',
              )}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="mfa-setup-password">
              {t('auth:Auth.CurrentPassword', 'Current Password')}
            </label>
            <input
              id="mfa-setup-password"
              type="password"
              className="form-control form-control-lg"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="d-grid gap-2">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              disabled={resetKey.isPending || password.length === 0}
              onClick={() => void startKeySetup(password)}
            >
              {t('Continue', 'Continue')}
            </button>
            {!isMandatory && (
              <Link to="/user-profile" className="btn btn-outline-secondary">
                {t('Cancel', 'Cancel')}
              </Link>
            )}
          </div>
        </>
      )}

      {stage === 'scan' && secret && (
        <>
          <p className="mb-3">
            {t(
              'auth:Auth.MFA.ScanQr',
              'Scan this code with your authenticator app, then enter the 6-digit code it shows.',
            )}
          </p>

          <div className="text-center mb-3">
            {secret.qrCodeSvg ? (
              <img
                src={`data:image/svg+xml;base64,${btoa(secret.qrCodeSvg)}`}
                alt={t('auth:Auth.MFA.ScanQr', 'Scan this code with your authenticator app')}
                style={{ width: 200, height: 200 }}
              />
            ) : null}
          </div>

          <div className="mb-3">
            <label className="form-label small text-muted">
              {t('auth:Auth.MFA.ManualKey', 'Or enter this key manually')}
            </label>
            <code className="d-block p-2 bg-body-tertiary rounded user-select-all text-center">
              {secret.formattedSharedKey}
            </code>
          </div>

          {/* Sets the expectation before the codes appear: they are the whole
              recovery story, so the user needs to be ready to keep them. */}
          <div className="alert alert-info small d-flex align-items-start" role="alert">
            <SpriteIcon name="info" size={16} className="sa-icon me-2 flex-shrink-0" />
            <div>
              {t(
                'auth:Auth.MFA.RecoveryCodesComing',
                'Once this is confirmed you will be given recovery codes. They are the only way back in if you lose your device, so have somewhere safe ready to keep them.',
              )}
            </div>
          </div>

          <form onSubmit={onSubmit} noValidate>
            <AuthField
              {...register('code')}
              label={t('auth:Auth.MFA.CodeLabel', 'Verification code')}
              large
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder={t('auth:Auth.MFA.CodePlaceholder', '123456')}
              error={errors.code?.message}
            />

            <div className="d-grid gap-2">
              <SubmitButton
                icon="check"
                label={t('auth:Auth.MFA.Enable', 'Turn on two-step verification')}
                pending={isSubmitting}
              />
              {!isMandatory && (
                <Link to="/user-profile" className="btn btn-outline-secondary">
                  {t('Cancel', 'Cancel')}
                </Link>
              )}
            </div>
          </form>
        </>
      )}

      {stage === 'recoveryCodes' && recoveryCodes && (
        <>
          <RecoveryCodeList codes={recoveryCodes} accountLabel={user?.email} />

          <div className="d-grid">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/', { replace: true })}
            >
              {t('auth:Auth.MFA.CodesSaved', 'I have saved my recovery codes')}
            </button>
          </div>
        </>
      )}

      {stage === 'scan' && !secret && (
        <div className="d-grid gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={resetKey.isPending}
            onClick={() => {
              showFormError('');
              void startKeySetup(undefined);
            }}
          >
            {t('Retry', 'Retry')}
          </button>
        </div>
      )}
    </AuthCard>
  );
}
